import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean, jsonb } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports
import { attachmentTypeEnum, attachmentSourceEnum } from "./schema-issues.js";
import { ccProjects } from "./schema-control-centre.js";

// ============================================================================
// TESTING WORKFLOW INTEGRATION (MODULE 3.0 - SPRINT-1.5)
// ============================================================================

// Test Result Enum
export const testResultEnum = pgEnum("test_result_enum", ["pass", "fail", "blocked", "skip"]);

// Sprint Status Enum (DEPRECATED - kept for backward compatibility during migration)
export const sprintStatusEnum = pgEnum("sprint_status_enum", ["planning", "development", "testing", "complete"]);

// Workflow Stage Enum (NEW - 13-stage workflow model)
export const workflowStageEnum = pgEnum("workflow_stage_enum", [
  "analysis",
  "architecture",
  "awaiting_approval",
  "database_migration",
  "list_implementation",
  "detail_implementation",
  "forms_implementation",
  "visual_testing",
  "test_execution",
  "results_verification",
  "pass_rate_validation",
  "manual_testing",
  "issue_resolution",
  "complete"
]);

// Checklist Type Enum
export const checklistTypeEnum = pgEnum("checklist_type_enum", ["manual", "automated"]);

// Test Item Type Enum - distinguishes automated (Playwright) vs manual test items
export const testItemTypeEnum = pgEnum("test_item_type_enum", ["automated", "manual"]);

// Screenshot Policy Enum - configurable screenshot capture behavior
export const screenshotPolicyEnum = pgEnum("screenshot_policy_enum", ["all", "failures_only", "none"]);

// Sprint Activity Type Enum
export const sprintActivityTypeEnum = pgEnum("sprint_activity_type_enum", [
  "stage_changed",
  "gate_passed",
  "gate_failed",
  "table_added",
  "status_changed",
  "sprint_created",
  "sprint_updated",
  "test_executed",
  "blocker_found",
  "blocker_resolved",
  "checkpoint_created",
  "document_added"
]);

// Sprints Table (Updated for 13-stage workflow model - Issue Resolution formalized)
export const sprints = pgTable("crm_x_sprints", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sprintNumber: varchar("sprint_number", { length: 50 }).notNull().unique(),
  sprintName: varchar("sprint_name", { length: 255 }),
  description: text("description"),
  projectId: uuid("project_id").references(() => ccProjects.id, { onDelete: "set null" }),
  module: varchar("module", { length: 100 }),

  // 13-Stage Workflow State
  currentStage: workflowStageEnum("current_stage").default("analysis"),
  stageStatus: varchar("stage_status", { length: 20 }).default("pending"),
  stageLastUpdated: timestamp("stage_last_updated").defaultNow(),
  checkpoint: varchar("checkpoint", { length: 100 }),

  // Gate Tracking (5 gates)
  gate1Passed: boolean("gate_1_passed").default(false),
  gate1Artifact: text("gate_1_artifact"),
  gate1VerifiedAt: timestamp("gate_1_verified_at"),
  gate2Passed: boolean("gate_2_passed").default(false),
  gate2Artifact: text("gate_2_artifact"),
  gate2VerifiedAt: timestamp("gate_2_verified_at"),
  gate3Passed: boolean("gate_3_passed").default(false),
  gate3Artifact: text("gate_3_artifact"),
  gate3VerifiedAt: timestamp("gate_3_verified_at"),
  gate4Passed: boolean("gate_4_passed").default(false),
  gate4Artifact: text("gate_4_artifact"),
  gate4VerifiedAt: timestamp("gate_4_verified_at"),
  gate5Passed: boolean("gate_5_passed").default(false),
  gate5Artifact: text("gate_5_artifact"),
  gate5VerifiedAt: timestamp("gate_5_verified_at"),

  // Test Metrics
  testPassRate: numeric("test_pass_rate", { precision: 5, scale: 2 }),
  testsPassing: integer("tests_passing").default(0),
  testsTotal: integer("tests_total").default(0),
  blockersCount: integer("blockers_count").default(0),

  // Evidence Tracking (JSONB stored as text in Drizzle)
  evidenceArtifacts: text("evidence_artifacts"),

  // Progress Tracking (kept for backward compatibility)
  tablesImplemented: text("tables_implemented").array(),
  totalTables: integer("total_tables").default(0),
  completedTables: integer("completed_tables").default(0),

  // Document Links (JSONB stored as text in Drizzle)
  documentLinks: text("document_links"),

  // Timeline
  startDate: date("start_date"),
  endDate: date("end_date"),

  // Audit
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Sprint Activity Log Table
export const sprintActivityLog = pgTable("crm_x_sprint_activity_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sprintId: uuid("sprint_id").notNull().references(() => sprints.id, { onDelete: "cascade" }),
  activityType: sprintActivityTypeEnum("activity_type").notNull(),

  // Activity Details
  title: varchar("title", { length: 200 }).notNull(),
  description: text("description"),

  // Metadata (JSONB for flexible data)
  metadata: jsonb("metadata").default({}),

  // Who made the change
  actorType: varchar("actor_type", { length: 50 }).default("agent"), // 'user' or 'agent'
  actorName: varchar("actor_name", { length: 100 }), // e.g., 'Builder Agent', 'John Doe'

  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Sprint Attachments Table (screenshots and documents)
export const sprintAttachments = pgTable("crm_x_sprint_attachments", {
  // Primary Key
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),

  // Foreign Key to Sprint
  sprintId: uuid("sprint_id").notNull().references(() => sprints.id, { onDelete: "cascade" }),

  // File Identity
  fileName: varchar("file_name", { length: 255 }).notNull(),
  filePath: varchar("file_path", { length: 500 }).notNull().unique(),
  fileSize: integer("file_size").notNull(),
  mimeType: varchar("mime_type", { length: 100 }).notNull(),

  // Optional Image Metadata
  width: integer("width"),
  height: integer("height"),

  // Classification (reuses enums from crm_x_issue_attachments)
  attachmentType: attachmentTypeEnum("attachment_type").notNull(),
  source: attachmentSourceEnum("source").notNull(),
  description: text("description"),

  // Metadata
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  uploadedByAgent: varchar("uploaded_by_agent", { length: 100 }),
});

// Testing Checklists Table
export const testingChecklists = pgTable("crm_x_testing_checklists", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  checklistNumber: varchar("checklist_number", { length: 50 }).notNull().unique(),
  title: varchar("title", { length: 255 }).notNull(),
  sprintId: uuid("sprint_id").notNull().references(() => sprints.id, { onDelete: "cascade" }),
  checklistType: checklistTypeEnum("checklist_type").notNull(),
  description: text("description"),

  // Progress Metrics (auto-calculated)
  totalSteps: integer("total_steps").default(0),
  stepsCompleted: integer("steps_completed").default(0),
  issuesFound: integer("issues_found").default(0),
  passRate: numeric("pass_rate", { precision: 5, scale: 2 }).default("0.00"),

  // Stage Workflow Fields (added for workflow_stage purpose)
  checklistPurpose: varchar("checklist_purpose", { length: 50 }).default("testing"), // 'testing' or 'workflow_stage'
  stageNumber: integer("stage_number"), // 1-13 for workflow_stage checklists
  workflowStage: varchar("workflow_stage", { length: 50 }), // Links to sprint currentStage

  // Audit
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Testing Checklist Items Table
export const testingChecklistItems = pgTable("crm_x_testing_checklist_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  checklistId: uuid("checklist_id").notNull(),

  // Test Identification
  testId: varchar("test_id", { length: 50 }).notNull(),
  stepNumber: integer("step_number"),

  // Test Categorization
  category: varchar("category", { length: 100 }),
  component: varchar("component", { length: 100 }),

  // Test Definition
  testDescription: text("test_description").notNull(),
  expectedResult: text("expected_result"),
  preconditions: text("preconditions"),

  // Question Type Configuration (Version 2.1 - JSONB stored as text in Drizzle)
  questionTypes: text("question_types").notNull(),  // JSONB array of question configs

  // Display Order
  sortOrder: integer("sort_order").notNull(),

  // Test Type Configuration (Unified Test Checklist Enhancement)
  testType: testItemTypeEnum("test_type").notNull().default("manual"),  // automated or manual
  testFilePath: varchar("test_file_path", { length: 500 }),  // Path to .spec.ts file for automated tests
  testLineNumber: integer("test_line_number"),  // Line number in source file for automated tests
  screenshotPolicy: screenshotPolicyEnum("screenshot_policy").notNull().default("failures_only"),  // When to capture screenshots
  testReportPath: varchar("test_report_path", { length: 500 }),  // Path to archived test report for traceability

  // Audit
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Test Step Executions Table
export const testStepExecutions = pgTable("crm_x_test_step_executions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  checklistItemId: uuid("checklist_item_id").notNull(),
  parentExecutionId: uuid("parent_execution_id"),  // For checkbox sub-executions

  // Execution Details
  executedBy: uuid("executed_by"),
  result: testResultEnum("result"),
  notes: text("notes"),

  // Question Response Data (JSONB)
  responseData: jsonb("response_data"),  // Structured responses from all question types

  // Auto-Generated Notes (from checkbox/dropdown selections)
  autoGeneratedNotes: text("auto_generated_notes"),

  // Issue Link
  issueId: uuid("issue_id"),

  // Screenshot Link (Unified Test Checklist Enhancement)
  screenshotId: uuid("screenshot_id"),  // FK to sprint_attachments for linked screenshot evidence

  // Stage Workflow Fields (added for workflow_stage execution tracking)
  evidenceArtifact: text("evidence_artifact"), // Path to gate evidence file
  completedBy: varchar("completed_by", { length: 50 }), // Agent name
  recoveryAttempted: boolean("recovery_attempted").default(false),
  recoveryAttempts: integer("recovery_attempts").default(0),

  // Execution Timestamp
  executedAt: timestamp("executed_at").defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS & TYPES - Testing Workflow Integration (SPRINT-1.5)
// ============================================================================

export const insertSprintSchema = createInsertSchema(sprints).omit({ id: true, createdAt: true, updatedAt: true });
export const updateSprintSchema = insertSprintSchema.partial();
export const selectSprintSchema = createSelectSchema(sprints);

export const insertSprintActivitySchema = createInsertSchema(sprintActivityLog).omit({ id: true, createdAt: true });
export const selectSprintActivitySchema = createSelectSchema(sprintActivityLog);

export const insertSprintAttachmentSchema = createInsertSchema(sprintAttachments).omit({ id: true, createdAt: true });
export const selectSprintAttachmentSchema = createSelectSchema(sprintAttachments);

export const insertTestingChecklistSchema = createInsertSchema(testingChecklists).omit({ id: true, checklistNumber: true, createdAt: true, updatedAt: true });
export const updateTestingChecklistSchema = insertTestingChecklistSchema.partial();
export const selectTestingChecklistSchema = createSelectSchema(testingChecklists);

export const insertTestingChecklistItemSchema = createInsertSchema(testingChecklistItems).omit({ id: true, createdAt: true });
export const updateTestingChecklistItemSchema = insertTestingChecklistItemSchema.partial();
export const selectTestingChecklistItemSchema = createSelectSchema(testingChecklistItems);

export const insertTestStepExecutionSchema = createInsertSchema(testStepExecutions).omit({ id: true, executedAt: true });
export const updateTestStepExecutionSchema = insertTestStepExecutionSchema.partial();
export const selectTestStepExecutionSchema = createSelectSchema(testStepExecutions);

// Testing Workflow Integration Types
export type InsertSprint = z.infer<typeof insertSprintSchema>;
export type UpdateSprint = z.infer<typeof updateSprintSchema>;
export type Sprint = typeof sprints.$inferSelect;

export type InsertSprintActivity = z.infer<typeof insertSprintActivitySchema>;
export type SprintActivity = typeof sprintActivityLog.$inferSelect;

export type InsertSprintAttachment = z.infer<typeof insertSprintAttachmentSchema>;
export type SprintAttachment = typeof sprintAttachments.$inferSelect;

export type InsertTestingChecklist = z.infer<typeof insertTestingChecklistSchema>;
export type UpdateTestingChecklist = z.infer<typeof updateTestingChecklistSchema>;
export type TestingChecklist = typeof testingChecklists.$inferSelect;

export type InsertTestingChecklistItem = z.infer<typeof insertTestingChecklistItemSchema>;
export type UpdateTestingChecklistItem = z.infer<typeof updateTestingChecklistItemSchema>;
export type TestingChecklistItem = typeof testingChecklistItems.$inferSelect;

export type InsertTestStepExecution = z.infer<typeof insertTestStepExecutionSchema>;
export type UpdateTestStepExecution = z.infer<typeof updateTestStepExecutionSchema>;
export type TestStepExecution = typeof testStepExecutions.$inferSelect;
